'use client'

import React, {
  useEffect,
  useMemo,
  useState,
} from 'react'

import Image from 'next/image'

import {
  getImageCandidates,
} from '@/lib/image'

import {
  API_CONFIG,
} from '@/config/api'

import Link from 'next/link'
import { LogOut } from 'lucide-react'

import {
  useBranding,
} from '@/hooks/useBranding'

import { JoinCommunityButton } from '@/components/community/JoinCommunityButton'
import { CommunityPaidAccessModal } from '@/components/community/CommunityPaidAccessModal'

type Access = {
  status:
    | 'approved'
    | 'pending'
    | 'banned'
    | 'admin'
    | 'partner'
    | 'owner'
    | 'none'
    | string

  gateType?:
    | 'none'
    | 'join'
    | 'pending'
    | 'banned'
    | 'paid'
    | 'community_paused'
    | string

  communityRecurringIds?: number[]
  hasActivePaidAccess?: boolean

  companyAccess?:
    | 'free'
    | 'paid'
    | string

  communityProductId?: number | null

  sellerMembershipActive?: boolean
  sellerMembershipExpiry?: string | null
  paymentUrl?: string | null
}

export function AccessGate({
  access,
  isOwner,
  isAdmin,
  sellerId,
  approvalRequired,
  tag = '',
}: {
  access: Access | null
  isOwner: boolean
  isAdmin: boolean
  sellerId: number
  approvalRequired: boolean
  tag?: string
}) {
  
  const [
    loggingOut,
    setLoggingOut,
  ] =
    useState(false)

  const {
    brand,
  } =
    useBranding(
      sellerId
    )

  const brandName =
    String(
      brand?.name ||
      'Hubee'
    ).trim()

  const isDark =
    typeof document !== 'undefined'
      ? document.documentElement.classList.contains(
          'dark'
        )
      : false

  const squareLogo =
    isDark
      ? brand?.logos
          ?.square_dark ||
        brand?.logos
          ?.square
      : brand?.logos
          ?.square

  const squareCandidates =
    useMemo(
      () =>
        getImageCandidates(
          squareLogo,
          {
            width: 72,
            height: 72,
            siteOrigin:
              API_CONFIG.BASE_URL,
            assetBaseUrl:
              process.env
                .NEXT_PUBLIC_ASSET_BASE_URL,
          }
        ),
      [
        squareLogo,
      ]
    )

  const [
    squareIndex,
    setSquareIndex,
  ] =
    useState(0)

  useEffect(() => {
    setSquareIndex(0)
  }, [
    squareLogo,
  ])

  const resolvedSquareLogo =
    squareCandidates[
      squareIndex
    ] || ''

  const status =
    access?.status || 'none'

  const gateType =
    access?.gateType || 'none'

  const isMember =
    status === 'approved'

  const isCommunityAdmin =
    status === 'admin'

  const isPending =
    status === 'pending'

  const isBanned =
    status === 'banned'

  const isJoined =
    isMember ||
    isPending

  /**
   * La membresía del SELLER en Hubee
   * está pausada.
   *
   * Este gate sí aplica al owner.
   */
  const shouldGateCommunityPaused =
    gateType === 'community_paused'

  /**
   * Comunidad pagada y el usuario
   * no tiene ninguna vía activa.
   */
  const shouldGatePaid =
    !isOwner &&
    !isAdmin &&
    gateType === 'paid'

  /**
   * Gate tradicional:
   * todavía no pertenece a comunidad.
   */
  const shouldGateClassic =
    !isOwner &&
    !isAdmin &&
    !isMember &&
    !isCommunityAdmin

  const shouldGate =
    shouldGateCommunityPaused ||
    shouldGatePaid ||
    shouldGateClassic

  useEffect(() => {
    if (!shouldGate) {
      return
    }

    const previousOverflow =
      document.body.style.overflow

    document.body.style.overflow =
      'hidden'

    return () => {
      document.body.style.overflow =
        previousOverflow
    }
  }, [shouldGate])

  async function handleLogout() {
    if (loggingOut) {
      return
    }

    try {
      setLoggingOut(
        true
      )

      await fetch(
        '/api/auth/logout',
        {
          method:
            'POST',

          credentials:
            'include',
        }
      ).catch(
        () => null
      )
    } finally {
      window.location.href =
        '/login'
    }
  }

  if (!shouldGate) {
    return null
  }

  /**
   * COMUNIDAD PAUSADA
   */
  if (
    shouldGateCommunityPaused
  ) {
    const ownerPaymentUrl =
      access?.paymentUrl ||
      `/setup?reason=community_paused&sellerId=${sellerId}`

    const title =
      isOwner
        ? 'Tu comunidad está pausada temporalmente'
        : 'Esta comunidad está pausada temporalmente'

    const message =
      isOwner
        ? 'Para volver a activar la experiencia de tus miembros, renueva tu acceso. Todo tu contenido, miembros y configuración siguen guardados.'
        : 'Por el momento no puedes acceder al contenido de esta comunidad. Tu progreso y acceso siguen guardados.'

    return (
      <div className="fixed inset-0 z-[9999]">
        <div
          className={[
            'absolute inset-0',
            'bg-background/65',
            'backdrop-blur-xl',
          ].join(' ')}
        />

        <div className="relative z-10 flex h-full w-full items-center justify-center px-5 py-8">
          <div
            className={[
              'w-full max-w-[460px]',
              'rounded-[28px]',
              'border border-border/60',
              'bg-background/92',
              'px-6 py-7 sm:px-8 sm:py-8',
              'text-center text-foreground',
              'shadow-[0_30px_100px_rgba(0,0,0,0.18)]',
              'backdrop-blur-2xl',
            ].join(' ')}
          >
            {/* BRAND */}
            <div className="flex flex-col items-center">
              {squareLogo &&
                resolvedSquareLogo ? (
                  <div className="flex h-[72px] w-[72px] items-center justify-center overflow-hidden rounded-2xl border border-border/50 bg-background shadow-sm">
                    <Image
                      key={
                        resolvedSquareLogo
                      }
                      src={
                        resolvedSquareLogo
                      }
                      alt={
                        brandName
                      }
                      width={72}
                      height={72}
                      unoptimized
                      onError={() => {
                        setSquareIndex(
                          current =>
                            current + 1
                        )
                      }}
                      className="h-full w-full object-contain p-2"
                    />
                  </div>
                ) : (
                <div className="flex h-[72px] w-[72px] items-center justify-center rounded-2xl bg-primary/10 text-2xl font-bold text-primary">
                  {brandName
                    .charAt(0)
                    .toUpperCase()}
                </div>
              )}

              <div className="mt-4 text-[13px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                {brandName}
              </div>
            </div>

            {/* STATUS */}
            <div className="mx-auto mt-6 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <span className="text-xl">
                ⏸
              </span>
            </div>

            <h1 className="mt-4 text-[22px] font-semibold leading-tight tracking-tight">
              {title}
            </h1>

            <p className="mx-auto mt-3 max-w-[360px] text-sm leading-6 text-muted-foreground">
              {message}
            </p>

            {isOwner ? (
              <>
                <div className="mt-7 space-y-3">
                  <Link
                    href={
                      ownerPaymentUrl
                    }
                    className={[
                      'flex h-12 w-full',
                      'items-center justify-center',
                      'rounded-xl',
                      'bg-primary',
                      'px-4',
                      'text-sm font-semibold',
                      'text-primary-foreground',
                      'shadow-sm',
                      'transition',
                      'hover:opacity-90',
                    ].join(' ')}
                  >
                    Reactivar mi comunidad
                  </Link>

                  <div className="px-3 text-xs leading-5 text-muted-foreground">
                    Una vez completado el pago,
                    la comunidad se reactivará automáticamente.
                  </div>
                </div>

                <div className="my-6 h-px bg-border/60" />

                <button
                  type="button"
                  onClick={
                    handleLogout
                  }
                  disabled={
                    loggingOut
                  }
                  className={[
                    'inline-flex items-center justify-center gap-2',
                    'text-sm font-medium',
                    'text-muted-foreground',
                    'transition',
                    'hover:text-foreground',
                    'disabled:opacity-50',
                  ].join(' ')}
                >
                  <LogOut className="h-4 w-4" />

                  {loggingOut
                    ? 'Saliendo…'
                    : 'Salir'}
                </button>
              </>
            ) : (
              <>
                <div className="mt-7 space-y-3">
                  <Link
                    href="https://hubee.app"
                    className={[
                      'flex h-12 w-full',
                      'items-center justify-center',
                      'rounded-xl',
                      'border border-border',
                      'px-4',
                      'text-sm font-semibold',
                      'transition',
                      'hover:bg-muted',
                    ].join(' ')}
                  >
                    Volver al inicio
                  </Link>
                </div>

                <div className="my-6 h-px bg-border/60" />

                <button
                  type="button"
                  onClick={
                    handleLogout
                  }
                  disabled={
                    loggingOut
                  }
                  className={[
                    'inline-flex items-center justify-center gap-2',
                    'text-sm font-medium',
                    'text-muted-foreground',
                    'transition',
                    'hover:text-foreground',
                    'disabled:opacity-50',
                  ].join(' ')}
                >
                  <LogOut className="h-4 w-4" />

                  {loggingOut
                    ? 'Saliendo…'
                    : 'Salir'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  /**
   * PAYWALL DE COMUNIDAD
   *
   * Ya NO recibe accessMode.
   *
   * El modal recibe ambas posibles
   * vías comerciales:
   *
   * - producto interno → pago único
   * - recurrings → suscripción
   *
   * y determina cuáles están activas.
   */
  if (shouldGatePaid) {
    return (
      <CommunityPaidAccessModal
        sellerId={sellerId}
        productId={
          Number(
            access?.communityProductId ||
              0
          ) || null
        }
        recurringIds={
          Array.isArray(
            access?.communityRecurringIds
          )
            ? access!.communityRecurringIds!
            : []
        }
      />
    )
  }

  /**
   * GATE CLÁSICO
   */

  let title = ''
  let message = ''

  if (isPending) {
    title =
      'Solicitud enviada'

    message =
      'Estamos esperando a que el administrador de la comunidad te dé acceso.'
  } else if (isBanned) {
    title =
      'Acceso restringido'

    message =
      'Tu acceso ha sido restringido temporalmente hasta que el administrador de la comunidad te dé acceso nuevamente.'
  } else if (approvalRequired) {
    title =
      'Acceso restringido'

    message =
      'Para acceder al contenido necesitas enviar una solicitud a la comunidad.'
  } else {
    title =
      'Comienza a navegar'

    message =
      'Únete a la comunidad para desbloquear el contenido y comenzar tu experiencia.'
  }

  return (
    <div className="fixed inset-0 z-[9999]">
      <div className="absolute inset-0 bg-background/65 backdrop-blur-xl" />

      <div className="relative z-10 flex h-full w-full items-center justify-center px-5 py-8">
        <div
          className={[
            'w-full max-w-[460px]',
            'rounded-[28px]',
            'border border-border/60',
            'bg-background/92',
            'px-6 py-7 sm:px-8 sm:py-8',
            'text-center text-foreground',
            'shadow-[0_30px_100px_rgba(0,0,0,0.18)]',
            'backdrop-blur-2xl',
          ].join(' ')}
        >
          {/* BRAND */}
          <div className="flex flex-col items-center">
            {squareLogo &&
            resolvedSquareLogo ? (
              <div className="flex h-[72px] w-[72px] items-center justify-center overflow-hidden rounded-2xl border border-border/50 bg-background shadow-sm">
                <Image
                  key={
                    resolvedSquareLogo
                  }
                  src={
                    resolvedSquareLogo
                  }
                  alt={
                    brandName
                  }
                  width={72}
                  height={72}
                  unoptimized
                  onError={() => {
                    setSquareIndex(
                      current =>
                        current + 1
                    )
                  }}
                  className="h-full w-full object-contain p-2"
                />
              </div>
            ) : (
              <div className="flex h-[72px] w-[72px] items-center justify-center rounded-2xl bg-primary/10 text-2xl font-bold text-primary">
                {brandName
                  .charAt(0)
                  .toUpperCase()}
              </div>
            )}

            <div className="mt-4 text-[13px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {brandName}
            </div>
          </div>

          {/* COPY */}
          <h1 className="mt-7 text-[22px] font-semibold leading-tight tracking-tight">
            {title}
          </h1>

          <p className="mx-auto mt-3 max-w-[360px] text-sm leading-6 text-muted-foreground">
            {message}
          </p>

          {/* ACTION */}
          {!isJoined &&
          !isBanned ? (
            <div className="mt-7">
              <JoinCommunityButton
                sellerId={sellerId}
                approvalRequired={
                  approvalRequired
                }
                tag={tag}
              />
            </div>
          ) : null}

          {/* LOGOUT */}
          <div className="my-6 h-px bg-border/60" />

          <button
            type="button"
            onClick={handleLogout}
            disabled={loggingOut}
            className={[
              'inline-flex items-center justify-center gap-2',
              'text-sm font-medium',
              'text-muted-foreground',
              'transition',
              'hover:text-foreground',
              'disabled:opacity-50',
            ].join(' ')}
          >
            <LogOut className="h-4 w-4" />

            {loggingOut
              ? 'Saliendo…'
              : 'Salir'}
          </button>
        </div>
      </div>
    </div>
  )
}